import { formatPointCoord } from "utils/data";
import { ResultElementSourceType, } from "../rs-workspace.types";
import { getTotalRuns } from "./calculations.utils";
import { getSimulationPreviewXAxisPointValue } from "./scenario.utils";
import { getTraceTemplate } from "shared/plotly/helpers";
export const getPointText = (pointData, sourceLineTitle, sourceLineUnit, sourceLineComputedName, sourceLineTiming, sourceType, displayFailedRunsData, totalRunsCount) => {
    const lineTitle = `<span style="font-size: 14px; font-weight: bold">${sourceLineTitle}</span><br>`;
    const { point, errorCount } = pointData;
    let text;
    //Tag "a" used for define value and get it on hover event for change innerHtml to display in correct format
    if (sourceType === ResultElementSourceType.ProcessData) {
        text = `<a>${point?.median}</a> ${sourceLineUnit}<br>`;
    }
    else {
        const sourceName = sourceLineComputedName
            ? `<br><span style="font-size: 14px; font-weight: 400; vertical-align: 9px;">${sourceLineComputedName}</span><br>`
            : "<br>";
        const timing = sourceLineTiming
            ? `<span style="font-size: 14px; font-weight: 400; vertical-align: 7px;">${sourceLineTiming}</span><br>`
            : "";
        const baseText = `${sourceName}${timing}Median - <a>${point?.median}</a> ${sourceLineUnit}<br>Mean - <a>${point?.average}</a> ${sourceLineUnit}<br>Spread - <a>${point?.spread}</a> ${sourceLineUnit}<br>SD - <a>${point?.sd}</a> ${sourceLineUnit}`;
        const failedRunsPercentage = Math.round((errorCount / (totalRunsCount ?? 1)) * 100);
        text = displayFailedRunsData
            ? `${failedRunsPercentage}% - ${errorCount} out of ${totalRunsCount} runs failed<br>${baseText}`
            : baseText;
    }
    return `${lineTitle}${text}`;
};
export const getProcessDataViewerTraces = (result, source, timeUnit, sourceLineTitle, sourceLineUnits, sourceLineComputedName) => {
    const totalRunsCount = result?.accuracy
        ? getTotalRuns({
            ...(result?.accuracy ?? {}),
            numberOfOptimisationSteps: null,
        }, false)
        : 0;
    const median = { x: [], y: [], text: [], customdata: [] };
    const average = { x: [], y: [], text: [], customdata: [] };
    const medianSpread = [];
    const averageSpread = [];
    const errorsGraph = { x: [], y: [], text: [], customdata: [] };
    let medianSpreadTraceTopHalf = { x: [], y: [] };
    let medianSpreadTraceBottomHalf = { x: [], y: [] };
    let averageSpreadTraceTopHalf = { x: [], y: [] };
    let averageSpreadTraceBottomHalf = { x: [], y: [] };
    result.points?.forEach((pointData, index, points) => {
        const { point, errorCount } = { ...pointData };
        const failedRunsPercentage = Math.round((errorCount / (totalRunsCount ?? 1)) * 100);
        const x = getSimulationPreviewXAxisPointValue(point?.x, timeUnit);
        errorsGraph.x.push(x);
        errorsGraph.y.push(failedRunsPercentage);
        errorsGraph.text.push(getPointText(pointData, sourceLineTitle ?? "", sourceLineUnits, sourceLineComputedName, undefined, source?.source?.type, true, totalRunsCount));
        const text = getPointText(pointData, sourceLineTitle ?? "", sourceLineUnits, sourceLineComputedName, undefined, source?.source?.type);
        median.x.push(x);
        median.y.push(formatPointCoord(point?.median));
        median.text.push(text);
        median.customdata.push(source?.id);
        average.x.push(x);
        average.y.push(formatPointCoord(point?.average));
        average.text.push(text);
        median.customdata.push(source?.id);
        if (point.median === null) {
            return;
        }
        // fill medianSpreadTrace
        medianSpreadTraceTopHalf.x.push(x);
        medianSpreadTraceBottomHalf.x.push(x);
        medianSpreadTraceTopHalf.y.push(formatPointCoord(point?.upperSpread));
        medianSpreadTraceBottomHalf.y.push(formatPointCoord(point?.lowerSpread));
        // fill averageSpreadTrace
        averageSpreadTraceTopHalf.x.push(x);
        averageSpreadTraceBottomHalf.x.push(x);
        averageSpreadTraceTopHalf.y.push(formatPointCoord(point?.upperSdSpread));
        averageSpreadTraceBottomHalf.y.push(formatPointCoord(point?.lowerSdSpread));
        const nextPointIsEmpty = points[index + 1]?.point?.median === null;
        const reachedLastPoint = index === points?.length - 1;
        if (nextPointIsEmpty || reachedLastPoint) {
            medianSpread.push({
                x: [...medianSpreadTraceTopHalf.x, ...medianSpreadTraceBottomHalf.x.reverse()],
                y: [...medianSpreadTraceTopHalf.y, ...medianSpreadTraceBottomHalf.y.reverse()],
            });
            averageSpread.push({
                x: [...averageSpreadTraceTopHalf.x, ...averageSpreadTraceBottomHalf.x.reverse()],
                y: [...averageSpreadTraceTopHalf.y, ...averageSpreadTraceBottomHalf.y.reverse()],
            });
            medianSpreadTraceTopHalf = { x: [], y: [] };
            medianSpreadTraceBottomHalf = { x: [], y: [] };
            averageSpreadTraceTopHalf = { x: [], y: [] };
            averageSpreadTraceBottomHalf = { x: [], y: [] };
        }
    });
    return { median, medianSpread, average, averageSpread, errorsGraph };
};
export const getDefaultGraphData = (result, color, visibility, index) => {
    const medianTrace = getTraceTemplate(index, "lines", color, visibility);
    medianTrace.x = result?.median?.x ?? [];
    medianTrace.y = result?.median?.y ?? [];
    medianTrace.customdata = (result?.median?.customdata ?? []);
    medianTrace.text = result?.median?.text ?? [];
    return medianTrace;
};
export const getGraphDataWithSpread = (type, data, result, color, visibility, index) => {
    const trace = getTraceTemplate(index, "lines", color, visibility);
    trace.x = result?.[type]?.x ?? [];
    trace.y = result?.[type]?.y ?? [];
    trace.customdata = (result?.[type]?.customdata ?? []);
    trace.text = result?.[type]?.text ?? [];
    data.push(trace);
    result?.[`${type}Spread`].forEach(({ x, y }) => {
        const trace = getTraceTemplate(index, "lines+spread", color, visibility);
        trace.x = x;
        trace.y = y;
        data.push(trace);
    });
    return data;
};
